import { useEffect, useRef, useState } from 'react'
import useBackendClient from '~/api/BackendClient/useBackendClient'
import { BackendHTTPError } from '~/api/shared/BackendHTTPError'
import { Poller } from '~/utils/hooks/usePollAsync'
import BackendClient from '../BackendClient/BackendClient'

export type UseBackendQueryArgs<TRequestParams, TResponseParams, TClient> = {
  backendQueryParams: {
    apiQuery: (client: TClient, params: TRequestParams) => Promise<TResponseParams>
    apiParams: TRequestParams
    skip?: boolean
    pollInterval?: number // in milliseconds
  }
}

type UseBackendQueryResponse<TResponseParams> = {
  data: TResponseParams | undefined
  loading: boolean
  error?: Error
  // Use this to update the data without re-fetching.
  // This is useful for optimistic updates based on user actions.
  setData: (data: TResponseParams | undefined) => void
}

export const useBackendQuery = <TRequestParams, TResponseParams, TClient>(
  client: TClient,
  apiQuery: (client: TClient, params: TRequestParams) => Promise<TResponseParams>,
  apiParams: TRequestParams,
  skip: boolean = false,
  pollInterval: number = 0,
): UseBackendQueryResponse<TResponseParams> => {
  const [data, setData] = useState<TResponseParams | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | undefined>(undefined)
  const poller = useRef<Poller | undefined>()

  const executeQuery = async () => {
    // Right now, we intentionally do not reset data to undefined and loading to true
    // when executeQuery is called again.
    //
    // One reason for this is that WellPage previously depended on data being cached
    // in Apollo to display a reasonable LoadingPlaceholder when navigating between wells.
    // Without the cache, keeping the stale data around is preferable.
    // Medium-term, we may want to redesign pages like WellPage so they can re-fetch just the part of the data that changes,
    // or alternatively, to re-implement a cache.
    try {
      const response = await apiQuery(client, apiParams)
      setData(response)
    } catch (e) {
      if (e instanceof BackendHTTPError) {
        console.error(e)
        setError({
          name: 'BackendHTTPError',
          message: e.message,
        })
      } else {
        console.error(e)
        setError({
          name: 'UnknownError',
          message: 'Unknown Error',
        })
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!skip) {
      executeQuery()
      if (pollInterval > 0) {
        poller.current = new Poller(executeQuery, pollInterval)
      }
    }

    return () => {
      if (poller.current) {
        poller.current.cancel()
        poller.current = undefined
      }
    }
  }, [apiParams, skip, pollInterval])

  if (skip) {
    return { data: undefined, loading: false, error: undefined, setData }
  }

  return { data, loading, error, setData }
}

export function useQuery<TRequestParams = any, TResponseParams = any>({
  backendQueryParams: {
    apiQuery,
    // If apiParams is an object, it must be memoized. It is a useEffect dependency in useBackendQuery.
    apiParams,
    skip,
    pollInterval,
  },
}: UseBackendQueryArgs<
  TRequestParams,
  TResponseParams,
  BackendClient
>): UseBackendQueryResponse<TResponseParams> {
  const client = useBackendClient()
  let { data, loading, error, setData } = useBackendQuery(
    client,
    apiQuery,
    apiParams,
    skip,
    pollInterval,
  )
  return { data, loading, error, setData }
}

export default useQuery
